//! Caches Pixi textures keyed by agent-connection UUID so the code-table
//! header can render the connection's brand thumbnail without hitting the
//! network on every redraw.
//!
//! The cache resolves the URL through the same precedence as
//! `resolveConnectionThumbnail` in `BrandLogo.tsx`: the connection row's own
//! `thumbnailUrl` first, falling back to the matching catalog tile.
//!
//! Subscribes to `agentConnectionsAtom` once at module load. When a
//! connection's resolved thumbnail URL changes (e.g. after a refetch picks
//! up a new admin-managed icon), the cached texture is invalidated and any
//! listener registered via `subscribeAgentConnectionTextureChanges` is
//! notified so the affected `TableName` instances can rebuild their sprite.

import { aiStore } from '@/app/ai/atoms/aiAnalystAtoms';
import { agentConnectionsAtom, getCatalogTileFromCacheAtom } from '@/app/atoms/agentConnectionAtom';
import { resolveConnectionThumbnail } from '@/app/ui/menus/AgentConnectionSetup/BrandLogo';
import { Texture } from 'pixi.js';

/**
 * Load an image URL into a Pixi `Texture`. We use a raw `HTMLImageElement`
 * and `Texture.from` instead of `Assets.load` because the presigned
 * thumbnail URLs have no file extension (just a content-hash) — Pixi's
 * extension-based parser detection bails out with "could not be loaded
 * as we don't know how to parse it" on those URLs.
 */
const loadImageAsTexture = (url: string): Promise<Texture> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      resolve(Texture.from(img));
    };
    img.onerror = (e) => reject(e);
    img.src = url;
  });

type ChangeListener = (connectionId: string) => void;

const listeners = new Set<ChangeListener>();
const textureCache = new Map<string, Promise<Texture | undefined>>();
// Last URL we resolved per connection id, used to detect when the cached
// texture is stale (admin updated the catalog icon, AI re-ran research and
// found a different favicon, etc.).
const lastResolvedUrl = new Map<string, string | null>();
// Connection ids that have ever been queried for a thumbnail. Tracked so
// the atom subscription below knows to fire change notifications when a
// connection's row arrives in the cache for the first time (the initial
// transition from "no URL known" to "URL available" — without this set,
// the first render of a code cell would be stuck on the language icon
// until something else triggered a redraw).
const requestedIds = new Set<string>();

const resolveUrlForConnectionId = (connectionId: string): string | null => {
  const connection = aiStore.get(agentConnectionsAtom).items.find((c) => c.uuid === connectionId);
  if (!connection) return null;
  const tile = aiStore.get(getCatalogTileFromCacheAtom)(connection.catalogSlug);
  return resolveConnectionThumbnail(connection, tile);
};

/**
 * Returns the cached texture for the given connection id, kicking off a
 * load if necessary. Resolves to `undefined` when no thumbnail is available
 * (no row yet, no URL on the row or tile) or the network/decode fails — in
 * which case the caller should fall back to the language icon.
 */
export const getAgentConnectionThumbnailTexture = (connectionId: string): Promise<Texture | undefined> => {
  requestedIds.add(connectionId);
  const url = resolveUrlForConnectionId(connectionId);
  if (!url) {
    textureCache.delete(connectionId);
    // Use `null` (not `undefined`) so the atom subscription can distinguish
    // "we know there's no URL right now" from "we've never looked".
    lastResolvedUrl.set(connectionId, null);
    return Promise.resolve(undefined);
  }

  // If the URL changed since we last loaded for this id, drop the stale
  // promise so the new URL is fetched.
  if (lastResolvedUrl.get(connectionId) !== url) {
    textureCache.delete(connectionId);
    lastResolvedUrl.set(connectionId, url);
  }

  const cached = textureCache.get(connectionId);
  if (cached) return cached;

  const promise = (async () => {
    try {
      return await loadImageAsTexture(url);
    } catch (e) {
      console.warn(`[agentConnectionThumbnailTextures] failed to load ${url}:`, e);
      // Don't poison the cache — the next call will retry.
      textureCache.delete(connectionId);
      return undefined;
    }
  })();

  textureCache.set(connectionId, promise);
  return promise;
};

export const subscribeAgentConnectionTextureChanges = (listener: ChangeListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const notifyChange = (connectionId: string): void => {
  for (const listener of listeners) {
    try {
      listener(connectionId);
    } catch (e) {
      console.warn('[agentConnectionThumbnailTextures] listener threw:', e);
    }
  }
};

// Invalidate cached entries whose resolved URL changed; emit to listeners
// so the on-grid table headers can swap textures without a full reload.
aiStore.sub(agentConnectionsAtom, () => {
  const items = aiStore.get(agentConnectionsAtom).items;
  const seenInItems = new Set<string>();

  // For every requested connection id, recompute the URL and emit when it
  // changes. This includes the first-load case (previousUrl === null,
  // meaning we previously couldn't resolve a URL).
  for (const id of requestedIds) {
    const item = items.find((c) => c.uuid === id);
    const tile = item ? aiStore.get(getCatalogTileFromCacheAtom)(item.catalogSlug) : null;
    const nextUrl = item ? resolveConnectionThumbnail(item, tile) : null;
    const previousUrl = lastResolvedUrl.get(id);
    if (previousUrl !== nextUrl) {
      textureCache.delete(id);
      lastResolvedUrl.set(id, nextUrl);
      notifyChange(id);
    }
  }

  for (const item of items) {
    seenInItems.add(item.uuid);
  }

  // Connections that disappeared from the cache (deleted from the team)
  // get their cached texture evicted so the next render falls back to the
  // language icon.
  for (const id of Array.from(textureCache.keys())) {
    if (!seenInItems.has(id)) {
      textureCache.delete(id);
      // Keep `lastResolvedUrl` set to null so the requested-id loop above
      // can detect a re-add later.
      if (lastResolvedUrl.get(id) !== null) {
        lastResolvedUrl.set(id, null);
        notifyChange(id);
      }
    }
  }
});
